import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RouterLink } from '@angular/router';
import { AutocompleteComponent, DynamicListComponent, type AutocompleteItem } from '@mm-aggregator/drf-dynamics';

import {
  FeeAnalyticsService,
  type FeeAnalyticsFilters,
  type FeeBreakdownResponse,
  type FeeConfigurationResponse,
  type FeeConfigurationRow,
  type PricingRulePayload,
  type FeeSummaryResponse,
} from '../../services/fee-analytics.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { formatHumanAmount, formatHumanDateTime } from '../../utils/admin-formatting';
import { MethodRouteOperationsService } from '../../services/method-route-operations.service';
import { UI } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';

@Component({
  selector: 'app-fees-hub-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, AutocompleteComponent, DynamicListComponent, ...UI],
  templateUrl: './fees-hub-page.component.html',
})
export class FeesHubPageComponent implements OnInit {
  @Input() entityConfig?: EntityConfig;

  private readonly feeAnalytics = inject(FeeAnalyticsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly opsCatalog = inject(MethodRouteOperationsService);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly loading = signal(true);
  readonly summary = signal<FeeSummaryResponse | null>(null);
  readonly currencyBreakdown = signal<FeeBreakdownResponse | null>(null);
  readonly operationBreakdown = signal<FeeBreakdownResponse | null>(null);
  readonly feeTypeBreakdown = signal<FeeBreakdownResponse | null>(null);
  readonly configurations = signal<FeeConfigurationRow[]>([]);
  readonly structureRows = signal<FeeConfigurationRow[]>([]);
  readonly structureTab = signal<'table' | 'graph'>('table');
  readonly showRuleForm = signal(false);
  readonly savingRule = signal(false);
  readonly editingRuleId = signal<string | null>(null);
  readonly formError = signal<string | null>(null);
  readonly stepError = signal<string | null>(null);
  readonly currentStep = signal(1);

  period = 'today';
  startDate = '';
  endDate = '';
  entityFilterId = '';
  entityFilterDisplay = '';
  currencyFilterCode = '';
  structureSearch = '';
  selectedScopeFilter = '';
  selectedFeeTypeFilter = '';
  selectedModeFilter = '';
  currencyDisplay = '';
  parentRuleDisplay = '';
  providerDisplay = '';
  entityDisplay = '';
  merchantDisplay = '';
  merchantApplicationDisplay = '';

  readonly ruleForm: PricingRulePayload = {
    name: '',
    description: '',
    scope: 'GLOBAL',
    operation_type: null,
    fee_type: 'OUR_FEE',
    amount_type: 'FIXED',
    value: '0.0000',
    currency: null,
    composition_mode: 'OVERRIDE',
    parent_rule: null,
    effective_from: null,
    effective_until: null,
    is_active: true,
    financial_entity: null,
    api_provider: null,
    merchant: null,
    merchant_application: null,
  };

  readonly scopeChoices = ['GLOBAL', 'PROVIDER', 'ENTITY', 'MERCHANT', 'MERCHANT_APPLICATION'];
  readonly feeTypeChoices = ['PROVIDER_FEE', 'OUR_FEE', 'MERCHANT_ADDITIONAL', 'PROMOTION'];
  readonly amountTypeChoices = ['FIXED', 'PERCENTAGE'];
  readonly compositionModeChoices = ['OVERRIDE', 'ADDITIVE'];
  readonly operationChoices = computed(() => this.opsCatalog.types().map((op) => op.value));
  readonly stepTitles = ['Scope & Target', 'Fee Definition', 'Schedule & Activation', 'Review & Confirm'];
  readonly pricingRulesListPath = 'finances/pricing-rules/';
  readonly helpText: Record<string, string> = {
    name: 'Optional internal name for easier discovery and future edits.',
    description: 'Describe business intent and override rationale for audit/readability.',
    scope: 'Scope determines which target entity this rule applies to.',
    fee_type: 'Select the fee bucket affected by this rule.',
    amount_type: 'Use FIXED for flat values and PERCENTAGE for percentage values.',
    value: 'For percentage, use values like 2.5 (for 2.5%). For fixed, use currency units.',
    currency: 'Required for FIXED amounts. Not used for percentage rules.',
    composition_mode: 'OVERRIDE replaces inherited rules. ADDITIVE stacks on top of parent/base rules.',
    parent_rule: 'Optional reference to the rule this one builds on.',
    operation_type: 'All means the rule applies to every operation type.',
    effective_from: 'Optional start datetime for rule activation.',
    effective_until: 'Optional end datetime. Leave empty for no expiry.',
    is_active: 'Inactive rules are kept for history but ignored in pricing resolution.',
    target: 'Pick the specific provider/entity/merchant/application for the selected scope.',
  };

  ngOnInit(): void {
    const financialEntityId = this.route.snapshot.queryParamMap.get('financial_entity_id');
    const currency = this.route.snapshot.queryParamMap.get('currency');
    const period = this.route.snapshot.queryParamMap.get('period');
    if (financialEntityId) {
      this.entityFilterId = financialEntityId;
      this.entityDisplay = financialEntityId;
    }
    if (currency) {
      this.currencyFilterCode = currency;
      this.currencyDisplay = currency;
    }
    if (period) {
      this.period = period;
    }
    this.reload();
  }

  private buildFilters(): FeeAnalyticsFilters {
    const filters: FeeAnalyticsFilters = { period: this.period };
    if (this.period === 'custom' && this.startDate && this.endDate) {
      filters.start_date = `${this.startDate}T00:00:00Z`;
      filters.end_date = `${this.endDate}T23:59:59Z`;
    }
    if (this.entityFilterId) filters.financial_entity_id = this.entityFilterId;
    if (this.currencyFilterCode) filters.currency = this.currencyFilterCode;
    return filters;
  }

  reload(): void {
    this.loading.set(true);
    const filters = this.buildFilters();

    this.feeAnalytics.getSummary(filters).subscribe({
      next: (summary) => {
        this.summary.set(summary);
        this.feeAnalytics.getBreakdown('currency', filters).subscribe((res) => this.currencyBreakdown.set(res));
        this.feeAnalytics.getBreakdown('operation_type', filters).subscribe((res) => this.operationBreakdown.set(res));
        this.feeAnalytics.getBreakdown('fee_type', filters).subscribe((res) => this.feeTypeBreakdown.set(res));
        this.feeAnalytics.getConfigurations(filters).subscribe((res) => {
          this.configurations.set(res.rows.slice(0, 12));
          this.structureRows.set(this.sortRulesByPrecedence(res.rows));
          this.loading.set(false);
        });
      },
      error: () => {
        this.summary.set(null);
        this.loading.set(false);
      },
    });
  }

  applyPeriod(period: string): void {
    this.period = period;
    this.reload();
  }

  formatAmount(amount: string, currency: string): string {
    return formatHumanAmount(amount, currency);
  }

  getBreakdownTotal(rows: Array<{ amount: string }>): number {
    return rows.reduce((acc, row) => acc + Number.parseFloat(row.amount || '0'), 0);
  }

  getBreakdownPercent(amount: string, total: number): number {
    if (total <= 0) return 0;
    return Math.max((Number.parseFloat(amount || '0') / total) * 100, 2);
  }

  readonly filteredStructureRows = computed(() => {
    const term = this.structureSearch.trim().toLowerCase();
    return this.structureRows().filter((row) => {
      if (this.selectedScopeFilter && row.scope !== this.selectedScopeFilter) return false;
      if (this.selectedFeeTypeFilter && row.fee_type !== this.selectedFeeTypeFilter) return false;
      if (this.selectedModeFilter && row.composition_mode !== this.selectedModeFilter) return false;
      if (!term) return true;
      const haystack = [
        row.name ?? '',
        row.scope,
        row.scope_target_label,
        row.fee_type,
        row.composition_mode,
        row.operation_type ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  });

  readonly graphNodes = computed(() =>
    this.filteredStructureRows().map((row, index) => ({
      id: row.id,
      x: 150 + row.scope_rank * 190,
      y: 70 + (index % 8) * 72,
      label: `${row.scope_target_label} | ${row.fee_type}`,
      mode: row.composition_mode,
    })),
  );

  readonly graphEdges = computed(() => {
    const existing = new Set(this.graphNodes().map((node) => node.id));
    return this.filteredStructureRows()
      .filter((row) => row.parent_rule_id && existing.has(row.parent_rule_id))
      .map((row) => ({ source: row.parent_rule_id as string, target: row.id, mode: row.composition_mode }));
  });

  setStructureTab(tab: 'table' | 'graph'): void {
    this.structureTab.set(tab);
  }

  openCreateRule(): void {
    this.resetRuleForm();
    this.editingRuleId.set(null);
    this.formError.set(null);
    this.stepError.set(null);
    this.currentStep.set(1);
    this.showRuleForm.set(true);
  }

  openEditRule(row: FeeConfigurationRow): void {
    this.ruleForm.name = row.name ?? '';
    this.ruleForm.description = row.description ?? '';
    this.ruleForm.scope = row.scope;
    this.ruleForm.operation_type = row.operation_type;
    this.ruleForm.fee_type = row.fee_type;
    this.ruleForm.amount_type = row.amount_type;
    this.ruleForm.value = row.value;
    this.ruleForm.currency = row.currency_id;
    this.ruleForm.composition_mode = row.composition_mode;
    this.ruleForm.parent_rule = row.parent_rule_id;
    this.ruleForm.effective_from = row.effective_from;
    this.ruleForm.effective_until = row.effective_until;
    this.ruleForm.is_active = row.is_active;
    this.ruleForm.financial_entity = row.financial_entity_id;
    this.ruleForm.api_provider = row.provider_id;
    this.ruleForm.merchant = row.merchant_id;
    this.ruleForm.merchant_application = row.merchant_application_id;
    this.currencyDisplay = row.currency ?? '';
    this.parentRuleDisplay = row.parent_rule_name ?? '';
    this.providerDisplay = row.provider_code ?? '';
    this.entityDisplay = row.financial_entity_code ?? '';
    this.merchantDisplay = row.merchant_name ?? '';
    this.merchantApplicationDisplay = row.merchant_application_name ?? '';
    this.editingRuleId.set(row.id);
    this.formError.set(null);
    this.stepError.set(null);
    this.currentStep.set(1);
    this.showRuleForm.set(true);
  }

  closeRuleForm(): void {
    this.showRuleForm.set(false);
    this.editingRuleId.set(null);
    this.formError.set(null);
    this.stepError.set(null);
  }

  saveRule(): void {
    this.savingRule.set(true);
    this.formError.set(null);
    const payload = this.cleanedPayload();
    const editingId = this.editingRuleId();
    const request$ = editingId
      ? this.feeAnalytics.updatePricingRule(editingId, payload)
      : this.feeAnalytics.createPricingRule(payload);
    request$.subscribe({
      next: () => {
        this.savingRule.set(false);
        this.closeRuleForm();
        this.reloadStructure();
      },
      error: (error) => {
        this.savingRule.set(false);
        this.formError.set(error?.error?.detail ?? 'Unable to save pricing rule.');
      },
    });
  }

  formatDate(date: string | null): string {
    if (!date) return '—';
    return formatHumanDateTime(date);
  }

  goToStep(step: number): void {
    if (step < 1 || step > 4) return;
    this.currentStep.set(step);
    this.stepError.set(null);
  }

  nextStep(): void {
    if (!this.validateStep(this.currentStep())) return;
    this.currentStep.set(Math.min(this.currentStep() + 1, 4));
    this.stepError.set(null);
  }

  prevStep(): void {
    this.currentStep.set(Math.max(this.currentStep() - 1, 1));
    this.stepError.set(null);
  }

  isStepCurrent(step: number): boolean {
    return this.currentStep() === step;
  }

  isStepComplete(step: number): boolean {
    return this.currentStep() > step;
  }

  resolveTargetLabel(): string {
    if (this.ruleForm.scope === 'PROVIDER') return this.providerDisplay || 'Not selected';
    if (this.ruleForm.scope === 'ENTITY') return this.entityDisplay || 'Not selected';
    if (this.ruleForm.scope === 'MERCHANT') return this.merchantDisplay || 'Not selected';
    if (this.ruleForm.scope === 'MERCHANT_APPLICATION') return this.merchantApplicationDisplay || 'Not selected';
    return 'Global (no target)';
  }

  resolveDescriptionLabel(): string {
    const txt = this.ruleForm.description?.trim() ?? '';
    return txt || '—';
  }

  getCompositionHint(): string {
    if (this.ruleForm.composition_mode === 'ADDITIVE') {
      return 'Additive rules stack with other applicable rules. Use with merchant/application scopes.';
    }
    return 'Override rules take precedence over less specific rules.';
  }

  onScopeChange(): void {
    this.clearProvider();
    this.clearEntity();
    this.clearMerchant();
    this.clearMerchantApplication();
  }

  onAmountTypeChange(): void {
    if (this.ruleForm.amount_type === 'PERCENTAGE') {
      this.clearCurrency();
    }
  }

  getNodeById(id: string): { id: string; x: number; y: number; label: string; mode: string } | undefined {
    return this.graphNodes().find((node) => node.id === id);
  }

  onPricingRuleRowSelect(row: Record<string, unknown>): void {
    const id = row['id'] ?? row['pk'];
    if (id != null) {
      void this.router.navigate(
        this.shellRoutes.commands('manage', 'pricing-rules', String(id)),
      );
    }
  }

  private cleanedPayload(): PricingRulePayload {
    return {
      ...this.ruleForm,
      operation_type: this.ruleForm.operation_type || null,
      currency: this.ruleForm.amount_type === 'PERCENTAGE' ? null : this.ruleForm.currency || null,
      parent_rule: this.ruleForm.parent_rule || null,
      effective_from: this.ruleForm.effective_from || null,
      effective_until: this.ruleForm.effective_until || null,
      api_provider: this.ruleForm.scope === 'PROVIDER' ? this.ruleForm.api_provider || null : null,
      financial_entity: this.ruleForm.scope === 'ENTITY' ? this.ruleForm.financial_entity || null : null,
      merchant: this.ruleForm.scope === 'MERCHANT' ? this.ruleForm.merchant || null : null,
      merchant_application: this.ruleForm.scope === 'MERCHANT_APPLICATION' ? this.ruleForm.merchant_application || null : null,
    };
  }

  private validateStep(step: number): boolean {
    if (step === 1) {
      if (!this.ruleForm.scope) {
        this.stepError.set('Scope is required.');
        return false;
      }
      if (this.ruleForm.scope === 'PROVIDER' && !this.ruleForm.api_provider) {
        this.stepError.set('Please select a provider for PROVIDER scope.');
        return false;
      }
      if (this.ruleForm.scope === 'ENTITY' && !this.ruleForm.financial_entity) {
        this.stepError.set('Please select an entity for ENTITY scope.');
        return false;
      }
      if (this.ruleForm.scope === 'MERCHANT' && !this.ruleForm.merchant) {
        this.stepError.set('Please select a merchant for MERCHANT scope.');
        return false;
      }
      if (this.ruleForm.scope === 'MERCHANT_APPLICATION' && !this.ruleForm.merchant_application) {
        this.stepError.set('Please select a merchant application for MERCHANT_APPLICATION scope.');
        return false;
      }
    }
    if (step === 2) {
      if (!this.ruleForm.fee_type || !this.ruleForm.amount_type || !this.ruleForm.composition_mode) {
        this.stepError.set('Fee type, amount type, and composition mode are required.');
        return false;
      }
      if (!this.ruleForm.value) {
        this.stepError.set('Value is required.');
        return false;
      }
      if (this.ruleForm.amount_type === 'FIXED' && !this.ruleForm.currency) {
        this.stepError.set('Currency is required for FIXED amount type.');
        return false;
      }
    }
    this.stepError.set(null);
    return true;
  }

  private resetRuleForm(): void {
    this.ruleForm.name = '';
    this.ruleForm.description = '';
    this.ruleForm.scope = 'GLOBAL';
    this.ruleForm.operation_type = null;
    this.ruleForm.fee_type = 'OUR_FEE';
    this.ruleForm.amount_type = 'FIXED';
    this.ruleForm.value = '0.0000';
    this.ruleForm.currency = null;
    this.ruleForm.composition_mode = 'OVERRIDE';
    this.ruleForm.parent_rule = null;
    this.ruleForm.effective_from = null;
    this.ruleForm.effective_until = null;
    this.ruleForm.is_active = true;
    this.ruleForm.financial_entity = null;
    this.ruleForm.api_provider = null;
    this.ruleForm.merchant = null;
    this.ruleForm.merchant_application = null;
    this.currencyDisplay = '';
    this.parentRuleDisplay = '';
    this.providerDisplay = '';
    this.entityDisplay = '';
    this.merchantDisplay = '';
    this.merchantApplicationDisplay = '';
  }

  private reloadStructure(): void {
    const filters: FeeAnalyticsFilters = { period: this.period };
    this.feeAnalytics.getConfigurations(filters).subscribe({
      next: (res: FeeConfigurationResponse) => this.structureRows.set(this.sortRulesByPrecedence(res.rows)),
    });
  }

  private sortRulesByPrecedence(rows: FeeConfigurationRow[]): FeeConfigurationRow[] {
    return [...rows].sort((a, b) => {
      if (a.scope_rank !== b.scope_rank) return b.scope_rank - a.scope_rank;
      if (a.fee_type !== b.fee_type) return a.fee_type.localeCompare(b.fee_type);
      return (a.scope_target_label || '').localeCompare(b.scope_target_label || '');
    });
  }

  selectCurrency(item: AutocompleteItem): void {
    this.ruleForm.currency = String(item.id);
    this.currencyDisplay = item.label;
  }

  clearCurrency(): void {
    this.ruleForm.currency = null;
    this.currencyDisplay = '';
  }

  selectParentRule(item: AutocompleteItem): void {
    this.ruleForm.parent_rule = String(item.id);
    this.parentRuleDisplay = item.label;
  }

  clearParentRule(): void {
    this.ruleForm.parent_rule = null;
    this.parentRuleDisplay = '';
  }

  selectProvider(item: AutocompleteItem): void {
    this.ruleForm.api_provider = String(item.id);
    this.providerDisplay = item.label;
  }

  clearProvider(): void {
    this.ruleForm.api_provider = null;
    this.providerDisplay = '';
  }

  selectEntity(item: AutocompleteItem): void {
    this.ruleForm.financial_entity = String(item.id);
    this.entityDisplay = item.label;
  }

  clearEntity(): void {
    this.ruleForm.financial_entity = null;
    this.entityDisplay = '';
  }

  selectMerchant(item: AutocompleteItem): void {
    this.ruleForm.merchant = String(item.id);
    this.merchantDisplay = item.label;
  }

  clearMerchant(): void {
    this.ruleForm.merchant = null;
    this.merchantDisplay = '';
  }

  selectMerchantApplication(item: AutocompleteItem): void {
    this.ruleForm.merchant_application = String(item.id);
    this.merchantApplicationDisplay = item.label;
  }

  clearMerchantApplication(): void {
    this.ruleForm.merchant_application = null;
    this.merchantApplicationDisplay = '';
  }
}
